/**
 * ApprovalCard component.
 *
 * The main registry-driven approval card component. Assembles header, sections
 * (via renderers), side effects, and action bar.
 *
 * This component operationalizes the "Flexibility over Structure" principle:
 * adding a new tool to Stream becomes a backend-only change, because this
 * component has no tool-specific code paths.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Loader2 } from 'lucide-react'

import { ApprovalCardHeader } from './ApprovalCardHeader'
import { ApprovalCardSection } from './ApprovalCardSection'
import { ApprovalCardSideEffects } from './ApprovalCardSideEffects'
import { ApprovalCardActionBar } from './ApprovalCardActionBar'
import { CardTranslationProvider, CardTranslateControl } from './CardTranslation'
import { useQueueStore } from '../../store'
import { useSidePanelContext } from '../SidePanelContext'
import type { ApprovalItem, ApprovalDetailResponse } from '../../types'

/**
 * Identifier of the execution-summary section in the registry-driven
 * card payload. When the modal is maximized we render this section in
 * the wide left column and everything else on the right.
 */
const EXECUTION_SUMMARY_SECTION_ID = 'execution_summary'

/**
 * Display priority for sections in the RIGHT column of the maximized
 * two-column layout. The backend usually emits args first and the
 * actionable artifact (e.g. the proposed email reply) second, but we
 * promote the proposed resolution above the tool arguments so the
 * human-readable summary leads the column. Sections not listed here
 * keep their relative backend order after these pinned ones.
 */
const SECONDARY_COLUMN_PRIORITY = [
  // Original inbound email (Task 4054d) — for send_email approvals the
  // customer's request leads the column (immediately after the
  // execution summary, which lives in the primary column) so the
  // operator reads context → proposed action.
  'original_email',
  // Original customer review (post_review_reply) — same rationale as
  // original_email: the review the agent is replying to (its text + star
  // rating) leads the column so the operator reads context → proposed public
  // reply. Without this entry the section sorts to the bottom of the column,
  // below Tool Arguments.
  'original_review',
  'proposed_resolution',
  // Reviewer Segmentation (Task 3702) — sits below the proposed resolution
  // so operators can quickly verify the support_manager's frustration
  // segment + engagement classification before approving the public reply,
  // but above the collapsible deep-dive sections (workflow context, step
  // executions) that are mainly used for debugging.
  'frustration_segment',
  'pending_message',
  'args',
  'workflow_context',
  'step_executions',
] as const

function secondaryColumnRank(id: string): number {
  const idx = (SECONDARY_COLUMN_PRIORITY as readonly string[]).indexOf(id)
  return idx === -1 ? SECONDARY_COLUMN_PRIORITY.length : idx
}

/**
 * Best-effort extraction of `outcome` from the execution-summary
 * section so it can be surfaced as a status chip in the card header.
 * Returns null when the section is missing, errored, streaming, or
 * doesn't expose an outcome field.
 */
function extractOutcome(
  sections: ApprovalDetailResponse['card']['sections'],
): string | null {
  const section = sections.find((s) => s.id === EXECUTION_SUMMARY_SECTION_ID)
  if (!section || section.error) return null
  const data = section.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const outcome = (data as Record<string, unknown>).outcome
  return typeof outcome === 'string' && outcome.trim().length > 0
    ? outcome
    : null
}

export interface ApprovalCardProps {
  /** The queue item. */
  item: ApprovalItem
  /** The card payload from the detail endpoint. */
  card: ApprovalDetailResponse['card']
  /** The current operator's ID. */
  currentOperatorId?: string
  /** Called when the user releases the claim. */
  onRelease?: () => void
  /** Called when the user approves. */
  onApprove?: () => void
  /** Called when the user suppresses. */
  onSuppress?: () => void
  /** Called when the user clicks reject. */
  onReject?: () => void
  /** Called when the user clicks escalate. */
  onEscalate?: () => void
  /** Called when the user clicks edit (for supports_edit items). */
  onEdit?: () => void
  /** Whether release is pending. */
  releasePending?: boolean
  /** Whether an action is pending. */
  actionPending?: boolean
  /** Which action is pending. */
  pendingAction?: 'approve' | 'suppress' | 'reject' | null
  /** Whether the card is in edit mode. */
  isEditing?: boolean
  /**
   * When true the action bar (Approve / Reject / Suppress) is hidden.
   * Used when rendering a resolved history item where actions are no
   * longer applicable.
   */
  readOnly?: boolean
  /**
   * Agent session id linked to this approval item, if any. When set,
   * the header surfaces a "View Agent Session" icon button.
   */
  sessionId?: string | null
}

export function ApprovalCard({
  item,
  card,
  currentOperatorId,
  onRelease,
  onApprove,
  onSuppress,
  onReject,
  // onEscalate and sessionId are retained on the props type for
  // backwards compatibility (callers still pass them) but are now
  // owned by the side-panel-level ApprovalHeaderActions component
  // instead of the card chrome.
  onEdit,
  releasePending,
  actionPending,
  pendingAction,
  isEditing,
  readOnly = false,
}: ApprovalCardProps) {
  const isOwnedByMe = item.owner_id === currentOperatorId

  // Layout context — tells us whether to switch to the two-column
  // wide-mode rendering or stick with the single-column sidebar shape.
  const { maximized } = useSidePanelContext()

  // Section collapse state - persisted within session
  const sectionCollapseState = useQueueStore((s) => s.sectionCollapseState)
  const setSectionCollapsed = useQueueStore((s) => s.setSectionCollapsed)

  /**
   * Get the collapse state for a section.
   *
   * Priority:
   * 1. Session-persisted state (if user has toggled)
   * 2. Default from section config
   */
  const getCollapsedState = React.useCallback(
    (sectionId: string, defaultCollapsed: boolean): boolean => {
      const key = `${item.id}:${sectionId}`
      const persisted = sectionCollapseState[key]
      return persisted !== undefined ? persisted : defaultCollapsed
    },
    [sectionCollapseState, item.id],
  )

  const handleSectionToggle = React.useCallback(
    (sectionId: string, collapsed: boolean) => {
      setSectionCollapsed(item.id, sectionId, collapsed)
    },
    [setSectionCollapsed, item.id],
  )

  // Streaming display hint (best-effort for Phase 1)
  const hasStreamingSection = card.sections.some(
    (section) =>
      section.data === null &&
      section.error === null &&
      item.status === 'pending',
  )

  // Edit-before-approve is "available only for deterministic automation steps"
  // Even if a tool's registry config sets supports_edit=true for an agent_tool item,
  // we must still suppress the Edit button.
  const showEditButton = card.supports_edit && item.source === 'automation'

  // Lift the execution-summary outcome up into the header as a status
  // chip so the headline state ("Pending review", "Success", "Failed")
  // sits where the eye lands first, rather than two scroll positions
  // into the body.
  const outcome = extractOutcome(card.sections)

  // Split sections for the two-column wide layout. The execution
  // summary is the dense human-readable narrative; the right column
  // hosts everything else (tool arguments, proposed resolution, …).
  // When there are no "other" sections — or we're in single-column
  // sidebar mode — fall back to the simple vertical stack.
  const primarySections = card.sections.filter(
    (s) => s.id === EXECUTION_SUMMARY_SECTION_ID,
  )
  // Order the right-column sections by `SECONDARY_COLUMN_PRIORITY`
  // rather than backend order — see the comment on that constant for
  // why. The sort is stable so sections sharing a rank (everything
  // not pinned) keep their relative backend order.
  const secondarySections = card.sections
    .filter((s) => s.id !== EXECUTION_SUMMARY_SECTION_ID)
    .slice()
    .sort((a, b) => secondaryColumnRank(a.id) - secondaryColumnRank(b.id))

  // When the execution-summary is collapsed its column is nothing but a
  // one-line chevron header — half the modal width wasted. Track whether
  // ALL primary sections are currently collapsed so we can switch layout.
  const primaryAllCollapsed =
    primarySections.length > 0 &&
    primarySections.every((s) => getCollapsedState(s.id, s.collapsed))

  // True → side-by-side execution-summary | content columns.
  // Requires maximized mode, both column sets present, AND the execution
  // summary actually being expanded (otherwise the left column is empty).
  const useTwoColumn =
    maximized &&
    primarySections.length > 0 &&
    secondarySections.length > 0 &&
    !primaryAllCollapsed

  // True → execution summary is collapsed in the maximized modal. Show
  // its header full-width above the secondary sections, then lay the
  // secondary sections out as a two-column grid (or full-width if there
  // is only one) so the modal space is used rather than wasted.
  const useWideGrid =
    maximized && primaryAllCollapsed && secondarySections.length > 0

  const renderSection = (section: ApprovalDetailResponse['card']['sections'][number]) => (
    <ApprovalCardSection
      key={section.id}
      section={section}
      isCollapsed={getCollapsedState(section.id, section.collapsed)}
      onToggle={handleSectionToggle}
    />
  )

  return (
    <CardTranslationProvider>
    <article
      role="article"
      aria-label={`${card.display.name} - ${item.breadcrumb}`}
      style={styles.card}
    >
      {/* Header */}
      <ApprovalCardHeader
        item={item}
        display={card.display}
        appLabel={card.app_label}
        currentOperatorId={currentOperatorId}
        onRelease={onRelease}
        releasePending={releasePending}
        outcome={outcome}
      />

      {/* Action bar at the top — operators frequently know the right
          call from the execution summary alone, so the buttons sit
          immediately under the header rather than at the bottom where
          they require scrolling past every detail section. Sticky
          so they stay reachable through long summaries.
          Hidden in readOnly mode (resolved history items). */}
      {!isEditing && !readOnly && (
        <ApprovalCardActionBar
          isOwned={isOwnedByMe}
          supportsEdit={showEditButton}
          onApprove={onApprove ?? (() => {})}
          onSuppress={onSuppress ?? (() => {})}
          onReject={onReject ?? (() => {})}
          onEdit={showEditButton ? onEdit : undefined}
          isPending={actionPending}
          pendingAction={pendingAction}
          position="top"
          buttonConfig={card.button_config}
        />
      )}

      {/* Single card-level translate toggle. Flips every translatable field on
          the card (review text, proposed reply, inbound email, ...) between the
          original language and English at once. Renders nothing unless at least
          one field looks non-English. */}
      <CardTranslateControl />

      {/* Sections layout — three modes:
          1. Two-column (maximized, execution summary expanded): execution
             summary on the left, actionable content on the right.
          2. Wide-grid (maximized, execution summary collapsed): the
             execution-summary header sits full-width above the content so
             the operator can expand it on demand; secondary sections fill
             the available modal width (two columns when there are multiple,
             full-width when there is only one).
          3. Single column (sidebar or no primary sections): simple stack. */}
      {useTwoColumn ? (
        <div style={styles.sectionsTwoCol}>
          <div style={styles.sectionsColumn}>
            {primarySections.map(renderSection)}
          </div>
          <div style={styles.sectionsColumn}>
            {secondarySections.map(renderSection)}
          </div>
        </div>
      ) : useWideGrid ? (
        <div style={styles.sections}>
          {/* Collapsed execution-summary header — accessible but not
              consuming half the modal width. */}
          {primarySections.map(renderSection)}
          {/* Secondary sections: two-column grid when there are multiple,
              full-width when there is only one. */}
          {secondarySections.length > 1 ? (
            <div style={styles.sectionsTwoCol}>
              {secondarySections.map(renderSection)}
            </div>
          ) : (
            secondarySections.map(renderSection)
          )}
        </div>
      ) : (
        <div style={styles.sections}>
          {card.sections.map(renderSection)}
        </div>
      )}

      {/* Streaming indicator */}
      {hasStreamingSection && (
        <div style={styles.streamingIndicator}>
          <Loader2 style={iconStyles.icon16} className="animate-spin" aria-hidden="true" />
          <span>Agent is still working...</span>
        </div>
      )}

      {/* Side Effects */}
      <ApprovalCardSideEffects effects={card.side_effects} />
    </article>
    </CardTranslationProvider>
  )
}

/**
 * Loading skeleton for the approval card.
 */
export function ApprovalCardSkeleton() {
  return (
    <div style={styles.card}>
      {/* Header skeleton */}
      <div style={styles.skeletonHeader}>
        <div style={styles.skeletonHeaderTop}>
          <div style={styles.skeletonHeaderLeft}>
            <div style={styles.skeletonIcon} className="animate-pulse" />
            <div style={styles.skeletonTitle} className="animate-pulse" />
          </div>
          <div style={styles.skeletonTimestamp} className="animate-pulse" />
        </div>
        <div style={styles.skeletonHeaderBottom}>
          <div style={styles.skeletonBreadcrumb} className="animate-pulse" />
          <div style={styles.skeletonBadge} className="animate-pulse" />
        </div>
      </div>

      {/* Section skeletons */}
      {[1, 2, 3].map((i) => (
        <div key={i} style={styles.skeletonSection}>
          <div style={styles.skeletonSectionTitle} className="animate-pulse" />
          <div style={styles.skeletonSectionContent}>
            <div style={styles.skeletonLine} className="animate-pulse" />
            <div style={styles.skeletonLineShort} className="animate-pulse" />
          </div>
        </div>
      ))}

      {/* Action bar skeleton */}
      <div style={styles.skeletonActionBar}>
        <div style={styles.skeletonButton} className="animate-pulse" />
        <div style={styles.skeletonButton} className="animate-pulse" />
        <div style={styles.skeletonButton} className="animate-pulse" />
      </div>
    </div>
  )
}

/**
 * Error state for when the card fails to load.
 */
export interface ApprovalCardErrorProps {
  error: Error | string
  onRetry?: () => void
}

export function ApprovalCardError({ error, onRetry }: ApprovalCardErrorProps) {
  const message = typeof error === 'string' ? error : error.message

  return (
    <div style={styles.errorContainer}>
      <div style={styles.errorTextContainer}>
        <p style={styles.errorTitle}>
          Failed to load approval details
        </p>
        <p style={styles.errorMessage}>{message}</p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={styles.retryButton}
          className="link-hover"
        >
          Try again
        </button>
      )}
    </div>
  )
}

const iconStyles: Record<string, CSSProperties> = {
  icon16: {
    width: '16px',
    height: '16px',
  },
}

const styles: Record<string, CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    // NOTE: padding-top is 0 here (not the natural 18px). The header
    // inside this article carries its own top padding so the sticky
    // action bar — a flex sibling of the header — has nothing
    // scrolling above it inside the article before reaching `top: 0`
    // of the scroll container. Without this the article's padding
    // region scrolls up and content from below the bar peeks through
    // the gap before the bar reaches the scrollport edge.
    padding: '0 20px 20px',
    // NOTE: deliberately no `overflow: hidden` here — the sticky action
    // bar inside this article needs the article to NOT establish its
    // own scroll container, otherwise position:sticky has nothing to
    // stick against. Long content is kept in-bounds by `wordBreak` on
    // descendants instead.
    wordBreak: 'break-word' as const,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  // Two-column sections grid used when the modal is maximized. 50/50
  // split: execution summary on the left, actionable artifacts (tool args,
  // proposed resolution) on the right. Even split because the right column
  // frequently carries substantial content (evidence summaries, proposed
  // email bodies) that benefits from as much horizontal space as the left.
  // The 28px column gap matches the maximized body's outer padding.
  sectionsTwoCol: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '28px',
    alignItems: 'start',
  },
  sectionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: 0,
  },
  streamingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  // Skeleton styles
  skeletonHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderBottom: '1px solid var(--border-default)',
    paddingBottom: '16px',
  },
  skeletonHeaderTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  skeletonHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  skeletonIcon: {
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-tertiary)',
  },
  skeletonTitle: {
    width: '128px',
    height: '24px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonTimestamp: {
    width: '80px',
    height: '20px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonHeaderBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  skeletonBreadcrumb: {
    width: '192px',
    height: '16px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonBadge: {
    width: '96px',
    height: '20px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  skeletonSectionTitle: {
    width: '96px',
    height: '16px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonSectionContent: {
    marginLeft: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  skeletonLine: {
    width: '100%',
    height: '16px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonLineShort: {
    width: '75%',
    height: '16px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  skeletonActionBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    borderTop: '1px solid var(--border-default)',
    paddingTop: '16px',
  },
  skeletonButton: {
    width: '112px',
    height: '36px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
  },
  // Error styles
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    background: 'rgba(239, 68, 68, 0.05)',
    padding: '24px',
    textAlign: 'center',
  },
  errorTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  errorTitle: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  errorMessage: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  retryButton: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
}

export default ApprovalCard
